import React, { useEffect, useState } from "react";
import { ShieldCheck, Plus, Search, Trash2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { aiApi, formatApiError } from "../../lib/ledgerApi";
import { toast } from "sonner";

export default function AdminCompliance() {
  const [list, setList] = useState([]);
  const [name, setName] = useState("");
  const [country, setCountry] = useState("XX");
  const [reason, setReason] = useState("Manual demo entry");
  const [type, setType] = useState("sanctions");

  const [screenName, setScreenName] = useState("");
  const [screenResult, setScreenResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  const load = async () => {
    try {
      const r = await aiApi.listSanctions();
      setList(r.entries || []);
    } catch (e) {
      toast.error("Load failed", { description: formatApiError(e.response?.data?.detail) });
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onAdd = async (e) => {
    e.preventDefault();
    if (!name) return;
    setBusy(true);
    try {
      await aiApi.addSanction({ name, country, reason, type });
      toast.success("Entry added");
      setName("");
      await load();
    } catch (e) {
      toast.error("Add failed", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setBusy(false);
    }
  };

  const onScreen = async (e) => {
    e.preventDefault();
    if (!screenName) return;
    setBusy(true);
    try {
      const r = await aiApi.screen({ name: screenName });
      setScreenResult(r);
    } catch (e) {
      toast.error("Screen failed", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    if (!pendingDelete) return;
    setBusy(true);
    try {
      await aiApi.deleteSanction(pendingDelete.id);
      toast.success("Removed", { description: pendingDelete.name });
      setPendingDelete(null);
      await load();
    } catch (e) {
      toast.error("Remove failed", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-6xl px-6 md:px-10 py-10 md:py-14" data-testid="admin-compliance-page">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">/ Super-admin · Compliance</div>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-medium flex items-center gap-3">
          <ShieldCheck className="w-6 h-6 text-muted-foreground" strokeWidth={1.5} /> Sanctions &amp; PEP list
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Mock OFAC analog. In production this hooks into OFAC, UN, EU and HMT lists. Adding a name here causes any
          business with a similar legal name to be blocked at submission.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
        <form onSubmit={onAdd} className="border border-border rounded-sm bg-card p-5 space-y-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Add sanction / PEP</div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Name</Label>
            <Input
              data-testid="comp-add-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="ShellCo Holdings"
              className="rounded-sm font-mono mt-1"
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Country</Label>
              <Input value={country} onChange={(e) => setCountry(e.target.value.toUpperCase())} maxLength={2} className="rounded-sm font-mono mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Type</Label>
              <Input value={type} onChange={(e) => setType(e.target.value)} className="rounded-sm font-mono mt-1" />
            </div>
            <div className="col-span-1" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Reason</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} className="rounded-sm font-mono mt-1" />
          </div>
          <Button type="submit" disabled={busy || !name} data-testid="comp-add-submit" className="rounded-sm">
            <Plus className="w-3 h-3 mr-1" strokeWidth={1.5} /> Add to list
          </Button>
        </form>

        <form onSubmit={onScreen} className="border border-border rounded-sm bg-card p-5 space-y-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Screen a name</div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Legal name</Label>
            <Input
              data-testid="comp-screen-name"
              value={screenName}
              onChange={(e) => setScreenName(e.target.value)}
              placeholder="DarkChannel Trading"
              className="rounded-sm font-mono mt-1"
            />
          </div>
          <Button type="submit" disabled={busy || !screenName} data-testid="comp-screen-submit" className="rounded-sm">
            <Search className="w-3 h-3 mr-1" strokeWidth={1.5} /> Screen now
          </Button>
          {screenResult && (
            <div data-testid="comp-screen-result" className="border-t border-border pt-3 text-xs space-y-1">
              <div className="font-mono">
                decision:{" "}
                <span className={
                  screenResult.decision === "block" ? "text-status-tampered"
                  : screenResult.decision === "flag" ? "text-amber-600 dark:text-amber-400"
                  : "text-status-ok"
                }>
                  {screenResult.decision}
                </span>
              </div>
              <div className="text-muted-foreground">{screenResult.rationale}</div>
              {screenResult.matches?.length > 0 && (
                <ul className="text-[11px] font-mono space-y-0.5 mt-2">
                  {screenResult.matches.map((m, i) => (
                    <li key={i}>· {m.name} ({m.country}) — score {Math.round(m.score * 100)}%</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </form>
      </div>

      <h2 className="text-lg font-medium mb-3">Active list ({list.length})</h2>
      <div className="border border-border rounded-sm bg-card divide-y divide-border max-h-[480px] overflow-auto">
        {list.map((e) => (
          <div key={e.id} data-testid={`comp-row-${e.id}`} className="p-3 grid grid-cols-12 gap-3 items-center text-sm">
            <div className="col-span-4 font-medium">{e.name}</div>
            <div className="col-span-1 font-mono text-xs text-muted-foreground">{e.country}</div>
            <div className="col-span-2 font-mono text-xs">
              {e.type}
              <span className={`ml-2 text-[10px] uppercase tracking-widest ${e.source === "seed" ? "text-muted-foreground" : "text-status-info"}`}>
                {e.source}
              </span>
            </div>
            <div className="col-span-4 text-xs text-muted-foreground truncate">{e.reason}</div>
            <div className="col-span-1 flex justify-end">
              {e.removable ? (
                <button
                  onClick={() => setPendingDelete(e)}
                  data-testid={`comp-delete-${e.id}`}
                  title="Remove entry"
                  className="w-7 h-7 inline-flex items-center justify-center border border-border rounded-sm hover:border-status-tampered hover:text-status-tampered transition-colors"
                >
                  <Trash2 className="w-3 h-3" strokeWidth={1.5} />
                </button>
              ) : (
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground" title="seed entry, protected">locked</span>
              )}
            </div>
          </div>
        ))}
      </div>

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove sanction entry?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-mono">{pendingDelete?.name}</span> ({pendingDelete?.country}) will be deleted.
              Existing transfers already blocked or held by this entry remain blocked/held.
              The action is logged to the compliance audit trail.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="comp-delete-cancel" className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              disabled={busy}
              data-testid="comp-delete-confirm"
              className="rounded-sm bg-status-tampered text-background hover:bg-status-tampered/90"
            >
              <Trash2 className="w-3 h-3 mr-1" strokeWidth={1.5} /> Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
